import { useState } from 'react';
import { Calendar, Loader2, Check, X } from 'lucide-react';
import { Card, ErrorState, useToast } from '@mustauto/ui/web';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@mustauto/ui/web';
import { SkeletonTable } from '@mustauto/ui/web';
import { EmptyState } from '@mustauto/ui/web';
import { useConfirm } from '@mustauto/ui/web';
import { formatApiError } from '@mustauto/utils';
import {
  useAppointments,
  useAppointmentActions,
  type Appointment,
} from '@/hooks/useAppointments';
import { useCan } from '@/hooks/useCan';

const STATUS_TABS = [
  { value: '', label: 'Todos' },
  { value: 'pending', label: 'Pendentes' },
  { value: 'confirmed', label: 'Confirmados' },
  { value: 'completed', label: 'Concluídos' },
  { value: 'cancelled', label: 'Cancelados' },
];

const STATUS_CONFIG: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  pending: { label: 'Pendente', variant: 'default' },
  confirmed: { label: 'Confirmado', variant: 'secondary' },
  completed: { label: 'Concluído', variant: 'outline' },
  cancelled: { label: 'Cancelado', variant: 'destructive' },
};

export function AppointmentsPage() {
  const [statusFilter, setStatusFilter] = useState('');
  // Confirmar e recusar é `agenda.gerir` (Fase 3); ver a agenda é de todos.
  const canManage = useCan()('agenda.gerir');

  const { data, isLoading, isError, error, refetch } = useAppointments({
    status: statusFilter || undefined,
  });
  const { confirm, cancel } = useAppointmentActions();

  const appointments = data?.appointments ?? [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Calendar size={24} />
        <h1 className="text-xl font-semibold">Agendamentos</h1>
        {appointments.length > 0 && (
          <span className="text-sm text-muted-foreground">({appointments.length})</span>
        )}
      </div>

      {/* Status tabs */}
      <div className="flex gap-2">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.value}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              statusFilter === tab.value
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
            onClick={() => setStatusFilter(tab.value)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Table */}
      {isLoading ? (
        <SkeletonTable rows={6} />
      ) : isError && appointments.length === 0 ? (
        /* Agenda vazia por falha faz o gestor planejar o dia como se não
           houvesse carro chegando. O `length === 0` importa: com a agenda já em
           cache, uma revalidação que falha ao voltar para a aba trocaria uma
           lista boa por uma tela de erro. */
        <ErrorState title="Não foi possível carregar a agenda" error={error} onRetry={() => refetch()} />
      ) : appointments.length === 0 ? (
        <EmptyState
          icon={<Calendar size={32} />}
          title="Nenhum agendamento encontrado"
          description={
            statusFilter
              ? 'Não há agendamentos com esse status no período. Tente outro filtro.'
              : 'Clientes B2C podem solicitar agendamentos pelo app. Quando solicitarem, eles aparecem aqui para você confirmar ou recusar.'
          }
        />
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Horário</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Telefone</TableHead>
                <TableHead>Veículo</TableHead>
                <TableHead>Descrição</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {appointments.map((apt) => {
                const cfg = STATUS_CONFIG[apt.status] ?? { label: apt.status, variant: 'outline' as const };
                return (
                  <TableRow key={apt.id}>
                    <TableCell>
                      {new Date(apt.date + 'T12:00:00').toLocaleDateString('pt-BR')}
                    </TableCell>
                    <TableCell className="font-mono">{apt.timeSlot}</TableCell>
                    <TableCell>{apt.clientName}</TableCell>
                    <TableCell className="font-mono text-sm">{apt.clientPhone ?? '-'}</TableCell>
                    <TableCell>
                      <div>
                        <span className="font-mono">{apt.vehiclePlate}</span>
                        <span className="ml-2 text-xs text-muted-foreground">{apt.vehicleName}</span>
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate text-sm">
                      {apt.serviceDescription}
                    </TableCell>
                    <TableCell>
                      <Badge variant={cfg.variant}>{cfg.label}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {apt.status === 'pending' && canManage && (
                        <div className="flex justify-end gap-2">
                          <button
                            className="flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600 disabled:opacity-50"
                            onClick={() => confirm.mutate(apt.id)}
                            disabled={confirm.isPending}
                          >
                            <Check size={14} />
                            Confirmar
                          </button>
                          <button
                            className="flex items-center gap-1 rounded-md bg-destructive px-3 py-1.5 text-xs font-medium text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
                            onClick={() => cancel.mutate(apt.id)}
                            disabled={cancel.isPending}
                          >
                            <X size={14} />
                            Cancelar
                          </button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
